'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import { connect } from 'react-redux';
import { List } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import partial from 'react-utils/partial';
import TimePickerButton from 'MeetingsLib/components/schedule/time/TimePickerButton';
import TimezoneSelect from 'MeetingsLib/components/schedule/time/TimezoneSelect';
import ZeroState from 'MeetingsLib/components/schedule/ZeroState';
import { getEvent as getEventSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAvailableStartTimesForSelectedDate as getAvailableStartTimesForSelectedDateSelector } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { isTroubleshootingSelector, getTroubleshootStartTimesForSelectedDate } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import isEmpty from 'transmute/isEmpty';
import H5 from 'UIComponents/elements/headings/H5';

class TimePicker extends Component {
  constructor(...args) {
    super(...args);

    this.getClassNames = () => {
      const {
        compact
      } = this.props;
      return "time-picker flex-column width-100 p-x-8 " + (compact ? "compact m-top-4" : 'm-y-8');
    };

    this.getTimeRangeValueLabel = time => {
      const {
        userTimezone
      } = this.props;
      return I18n.moment(time).tz(userTimezone).format('LT');
    };

    this.handleSetEventDateTime = time => {
      const {
        setEventDateTime
      } = this.props;
      return partial(setEventDateTime, time);
    };

    this.shouldShowNoTimesMessage = () => {
      const {
        availableStartTimeOptions,
        isTroubleshooting,
        troubleshootStartTimes
      } = this.props;

      if (isTroubleshooting) {
        return isEmpty(troubleshootStartTimes);
      }

      return availableStartTimeOptions.count() === 0;
    };

    this.renderTroubleshootTimeButtons = () => {
      const {
        branding,
        selectedTime,
        troubleshootStartTimes,
        userTimezone,
        selectedDate
      } = this.props;
      return troubleshootStartTimes.map(troubleshootStartTime => {
        const {
          conflictingEvents: [conflictingEvent = {}]
        } = troubleshootStartTime;
        return /*#__PURE__*/_jsx(TimePickerButton, {
          branding: branding,
          disabled: !troubleshootStartTime.available,
          selectedTime: selectedTime,
          time: troubleshootStartTime.startMillisUtc,
          unavailableReason: troubleshootStartTime.reason,
          userTimezone: userTimezone,
          handleSetEventDateTime: this.handleSetEventDateTime(troubleshootStartTime.startMillisUtc),
          conflictingEvent: conflictingEvent,
          selectedDate: selectedDate
        }, troubleshootStartTime.startMillisUtc);
      });
    };

    this.renderTimePickerButtons = () => {
      const {
        availableStartTimeOptions,
        selectedTime,
        userTimezone,
        branding,
        selectedDate
      } = this.props;
      return availableStartTimeOptions.sort().map(time => {
        return /*#__PURE__*/_jsx(TimePickerButton, {
          disabled: false,
          branding: branding,
          handleSetEventDateTime: this.handleSetEventDateTime(time),
          selectedTime: selectedTime,
          time: time,
          userTimezone: userTimezone,
          selectedDate: selectedDate
        }, time);
      });
    };

    this.renderInputs = () => {
      const {
        isTroubleshooting,
        TimePickerMarker,
        availableStartTimeOptions
      } = this.props;
      const buttons = isTroubleshooting ? this.renderTroubleshootTimeButtons() : this.renderTimePickerButtons();
      const sortedTimes = availableStartTimeOptions.sort();
      const maxVal = this.getTimeRangeValueLabel(sortedTimes.last());
      const minVal = this.getTimeRangeValueLabel(sortedTimes.first());
      return /*#__PURE__*/_jsxs("div", {
        role: "region",
        "aria-labelledby": "available-times",
        id: "available-times-region",
        "aria-controls": "meeting-confirmation-form",
        className: "time-list-container p-x-1 overflow-y-auto",
        "aria-valuemax": maxVal,
        "aria-valuemin": minVal,
        children: [buttons, /*#__PURE__*/_jsx(TimePickerMarker, {})]
      });
    };

    this.renderTimezoneSelect = () => {
      const {
        setTimezone,
        userTimezone,
        branding
      } = this.props;
      return /*#__PURE__*/_jsx(TimezoneSelect, {
        style: {
          width: 'auto'
        },
        setTimezone: setTimezone,
        userTimezone: userTimezone,
        branding: branding,
        placement: "bottom"
      });
    };
  }

  render() {
    const {
      duration,
      profile,
      TimePickerMarker,
      isRescheduling
    } = this.props;

    if (!duration) {
      return null;
    }

    if (this.shouldShowNoTimesMessage()) {
      return /*#__PURE__*/_jsx("div", {
        className: "align-center height-100 p-x-10",
        children: /*#__PURE__*/_jsx(ZeroState, {
          profile: profile,
          TimePickerMarker: TimePickerMarker
        })
      });
    }

    return /*#__PURE__*/_jsxs("div", {
      className: this.getClassNames(),
      children: [/*#__PURE__*/_jsx(H5, {
        "aria-level": 2,
        className: "m-bottom-1",
        id: "available-times",
        children: isRescheduling ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.timePicker.rescheduleHeader"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.timePicker.header"
        })
      }), this.renderTimezoneSelect(), this.renderInputs()]
    });
  }

}

TimePicker.propTypes = {
  // passed
  branding: PropTypes.instanceOf(Branding).isRequired,
  compact: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile).isRequired,
  // loaded
  availableStartTimeOptions: PropTypes.instanceOf(List).isRequired,
  duration: PropTypes.number,
  selectedTime: PropTypes.number,
  setEventDateTime: PropTypes.func.isRequired,
  setTimezone: PropTypes.func.isRequired,
  troubleshootStartTimes: PropTypes.instanceOf(List).isRequired,
  userTimezone: PropTypes.string.isRequired,
  isTroubleshooting: PropTypes.bool.isRequired,
  TimePickerMarker: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  isRescheduling: PropTypes.bool
};
export default connect(state => ({
  // Rerenders component in response to translation
  activeLocale: state.activeLocale,
  troubleshootStartTimes: getTroubleshootStartTimesForSelectedDate(state),
  isTroubleshooting: isTroubleshootingSelector(state),
  availableStartTimeOptions: getAvailableStartTimesForSelectedDateSelector(state),
  duration: getEventSelector(state).duration,
  isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
  selectedTime: getEventSelector(state).dateTime,
  selectedDate: getEventSelector(state).dateString,
  userTimezone: state.userTimezone,
  isRescheduling: state.isRescheduling
}), Object.assign({}, FormActions))(TimePicker);